import { useEffect, useRef, useState } from "react";

const PAPER = "#f4ead5";
const INK = "#2b1810";
const RUST = "#8b2e2a";
const FADED = "#7a6a52";

const TEMPLATES = [
  { id: "character",  label: "The Character",   tagline: "A figure of repute (or disrepute).",
    framing: "A character sketch limerick about an eccentric individual associated with the seed word. Open in the classic 'There once was a / There was a / I once knew a' style." },
  { id: "mishap",     label: "The Mishap",      tagline: "A small disaster, finely etched.",
    framing: "A limerick recounting a comic misadventure or accident involving the seed word. The fifth line should deliver the punchline." },
  { id: "philosopher",label: "The Philosopher", tagline: "Wisdom, or its impostor.",
    framing: "A contemplative limerick that ponders the seed word as if it were a great mystery — mock-philosophical, dryly absurd." },
  { id: "lovesick",   label: "The Lovesick",    tagline: "Affection, gone awry.",
    framing: "A romantic limerick where the seed word is the object (or cause) of a love gone sideways. Tender, then ridiculous." },
  { id: "counsel",    label: "The Wise Counsel",tagline: "Advice you should ignore.",
    framing: "A limerick that offers earnest-sounding advice concerning the seed word — advice that proves to be terrible." },
  { id: "boast",      label: "The Boast",       tagline: "A brag, slightly punctured.",
    framing: "A limerick boasting of an outlandish exploit involving the seed word. The narrator's bravado collapses by the last line." },
];

const POS = ["noun", "verb", "adjective", "adverb"];

function buildPrompt({ word, pos, template }) {
  const safeWord = word.replace(/"/g, "'").replace(/\s+/g, " ").trim();
  return (
    "You are an expert limerick writer in the tradition of Edward Lear.\n\n" +
    "Seed word: \"" + safeWord + "\" (used as a " + pos + ").\n" +
    "Template: " + template.label + " — " + template.framing + "\n\n" +
    "Rules:\n" +
    "1. Exactly 5 lines. Rhyme scheme AABBA.\n" +
    "2. Lines 1, 2, and 5 rhyme together (A); lines 3 and 4 rhyme together (B).\n" +
    "3. Lines 1, 2, 5 are longer (7-10 syllables). Lines 3, 4 are shorter (5-7 syllables).\n" +
    "4. Use anapestic meter where natural; rhythm matters more than rigid scansion.\n" +
    "5. The seed word (or a clearly recognizable form of it) must appear at least once.\n" +
    "6. Wit over crudity. PG-13 at most.\n\n" +
    "Reply with ONLY a JSON object. No markdown, no backticks, no preamble. Start with { end with }.\n" +
    'Schema: {"title":"A short evocative title","lines":["line 1","line 2","line 3","line 4","line 5"]}'
  );
}

function extractJSON(text) {
  if (!text) return null;
  const tries = [
    () => JSON.parse(text.trim()),
    () => JSON.parse(text.replace(/```json/gi, "").replace(/```/g, "").trim()),
    () => {
      const s = text.indexOf("{"), e = text.lastIndexOf("}");
      if (s >= 0 && e > s) return JSON.parse(text.slice(s, e + 1));
    },
  ];
  for (const fn of tries) {
    try {
      const r = fn();
      if (r && Array.isArray(r.lines) && r.lines.length === 5) return r;
    } catch (_) {}
  }
  return null;
}

async function callAnthropic(prompt, maxAttempts = 4) {
  let lastErr = "";
  for (let i = 0; i < maxAttempts; i++) {
    if (i > 0) await new Promise(r => setTimeout(r, 400 * i));
    try {
      const res = await fetch("/api/anthropic", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 600,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      const raw = await res.text();
      const data = JSON.parse(raw);
      if (data.error) { lastErr = data.error.message || JSON.stringify(data.error); continue; }
      const text = (data.content || []).map(b => b.text || "").join("").trim();
      const parsed = extractJSON(text);
      if (parsed) return parsed;
      lastErr = "Unparseable: " + text.slice(0, 120);
    } catch (e) {
      lastErr = String(e.message || e);
    }
  }
  throw new Error("Failed after " + maxAttempts + " attempts. Last: " + lastErr);
}

const KEYFRAMES = `
@keyframes lp-fade-in {
  from { opacity: 0; transform: translateY(6px); filter: blur(1px); }
  to   { opacity: 1; transform: translateY(0);   filter: blur(0); }
}
@keyframes lp-stamp {
  0%   { opacity: 0; transform: scale(1.06); }
  60%  { opacity: 1; transform: scale(0.99); }
  100% { opacity: 1; transform: scale(1); }
}
`;

export default function Limericks() {
  const [word, setWord] = useState("");
  const [pos, setPos] = useState("noun");
  const [template, setTemplate] = useState(TEMPLATES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [poem, setPoem] = useState(null);
  const [revealed, setRevealed] = useState(0);
  const timersRef = useRef([]);

  function clearTimers() {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }
  useEffect(() => () => clearTimers(), []);

  async function generate() {
    const w = word.trim();
    if (!w || loading) return;
    clearTimers();
    setLoading(true);
    setError("");
    setPoem(null);
    setRevealed(0);
    try {
      const result = await callAnthropic(buildPrompt({ word: w, pos, template }));
      setPoem(result);
      result.lines.forEach((_, i) => {
        const t = setTimeout(() => setRevealed(n => Math.max(n, i + 1)), 350 + i * 650);
        timersRef.current.push(t);
      });
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ minHeight: "calc(100vh - 60px)", background: PAPER, color: INK, fontFamily: "'Hoefler Text', 'Baskerville', Garamond, Georgia, serif", padding: "40px 16px 80px",
                  backgroundImage: "radial-gradient(circle at 20% 10%, rgba(139,46,42,0.04), transparent 50%), radial-gradient(circle at 80% 80%, rgba(43,24,16,0.05), transparent 55%)" }}>
      <style>{KEYFRAMES}</style>

      <div style={{ maxWidth: "640px", margin: "0 auto" }}>

        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ fontVariant: "small-caps", letterSpacing: "6px", fontSize: "12px", color: FADED }}>est. mcmxxv</div>
          <div style={{ fontFamily: "'Hoefler Text', 'Didot', 'Bodoni 72', Georgia, serif", fontWeight: "bold", fontSize: "64px", lineHeight: 1, margin: "8px 0 4px" }}>
            <span style={{ fontStyle: "italic" }}>The</span> Limerickery
          </div>
          <div style={{ fontVariant: "small-caps", letterSpacing: "5px", fontSize: "12px", color: FADED }}>a small press for verses of dubious merit</div>
          <Divider />
        </div>

        <div style={{ border: "1px double " + INK, padding: "22px 22px 18px", background: "rgba(255,255,255,0.25)" }}>
          <Label>Subject</Label>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <input
              value={word}
              onChange={e => setWord(e.target.value)}
              onKeyDown={e => e.key === "Enter" && generate()}
              placeholder="a word, any word"
              style={{ flex: "1 1 220px", background: "transparent", border: "none", borderBottom: "1px solid " + INK, padding: "8px 4px", color: INK, fontFamily: "inherit", fontSize: "22px", fontStyle: "italic", outline: "none" }}
            />
            <select
              value={pos}
              onChange={e => setPos(e.target.value)}
              style={{ background: "transparent", border: "1px solid " + INK, padding: "6px 10px", color: INK, fontFamily: "inherit", fontSize: "14px", fontVariant: "small-caps", letterSpacing: "2px", cursor: "pointer" }}
            >
              {POS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div style={{ height: "20px" }} />

          <Label>Choose a Template</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px" }}>
            {TEMPLATES.map(t => {
              const selected = t.id === template.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTemplate(t)}
                  style={{ textAlign: "left", padding: "10px 12px", border: "1px solid " + INK, background: selected ? INK : "transparent", color: selected ? PAPER : INK, cursor: "pointer", fontFamily: "inherit" }}
                >
                  <div style={{ fontVariant: "small-caps", letterSpacing: "2px", fontSize: "13px" }}>{t.label}</div>
                  <div style={{ fontSize: "11px", fontStyle: "italic", color: selected ? "rgba(244,234,213,0.75)" : FADED, marginTop: "2px" }}>{t.tagline}</div>
                </button>
              );
            })}
          </div>

          <div style={{ height: "20px" }} />

          <button
            onClick={generate}
            disabled={loading || !word.trim()}
            style={{ width: "100%", padding: "14px", background: INK, color: PAPER, border: "none", fontFamily: "inherit", fontVariant: "small-caps", letterSpacing: "5px", fontSize: "15px", cursor: loading ? "wait" : "pointer", opacity: (loading || !word.trim()) ? 0.5 : 1 }}
          >
            {loading ? "Setting the type…" : "Press the Verse"}
          </button>

          {error && (
            <div style={{ marginTop: "16px", border: "1px solid " + RUST, padding: "12px", color: RUST, fontStyle: "italic", fontSize: "13px" }}>
              <span style={{ fontVariant: "small-caps", letterSpacing: "3px", marginRight: "8px" }}>errata.</span>
              {error}
            </div>
          )}
        </div>

        {poem && (
          <div style={{ marginTop: "32px", animation: "lp-stamp 600ms ease-out both" }}>
            <Divider />
            <div style={{ textAlign: "center", marginTop: "8px", marginBottom: "18px" }}>
              <div style={{ fontVariant: "small-caps", letterSpacing: "4px", fontSize: "11px", color: FADED }}>{template.label}</div>
              <div style={{ fontFamily: "'Hoefler Text', 'Didot', Georgia, serif", fontStyle: "italic", fontSize: "30px", marginTop: "6px" }}>{poem.title}</div>
            </div>

            <div style={{ border: "1px solid " + INK, borderWidth: "2px 1px", padding: "32px 28px", background: "rgba(255,255,255,0.35)", textAlign: "center", fontSize: "22px", lineHeight: 1.7, fontFamily: "'Hoefler Text', 'Baskerville', Georgia, serif" }}>
              {poem.lines.map((line, i) => {
                if (i >= revealed) return <div key={i} style={{ minHeight: "1.7em" }}>&nbsp;</div>;
                const isFirst = i === 0;
                const indent = (i === 2 || i === 3) ? "1.5em" : "0";
                return (
                  <div key={i} style={{ marginLeft: indent, animation: "lp-fade-in 700ms ease-out both", textShadow: "0 0 0.6px " + INK }}>
                    {isFirst ? <DropCap text={line} /> : line}
                  </div>
                );
              })}
            </div>

            <div style={{ textAlign: "center", marginTop: "16px", color: FADED, fontVariant: "small-caps", letterSpacing: "4px", fontSize: "11px" }}>
              ❦ &nbsp; printed this day at the limerickery &nbsp; ❦
            </div>
          </div>
        )}

        {!poem && !loading && !error && (
          <div style={{ textAlign: "center", marginTop: "36px", color: FADED, fontStyle: "italic", fontSize: "14px" }}>
            Furnish a word and a template; the press shall do the rest.
          </div>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontVariant: "small-caps", letterSpacing: "4px", fontSize: "11px", color: FADED, marginBottom: "8px" }}>
      {children}
    </div>
  );
}

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", margin: "14px 0", color: FADED }}>
      <span style={{ flex: 1, height: 0, borderTop: "1px solid " + INK, opacity: 0.4 }} />
      <span style={{ fontSize: "16px" }}>❦</span>
      <span style={{ flex: 1, height: 0, borderTop: "1px solid " + INK, opacity: 0.4 }} />
    </div>
  );
}

function DropCap({ text }) {
  if (!text) return null;
  const first = text.charAt(0);
  const rest = text.slice(1);
  return (
    <span>
      <span style={{ fontFamily: "'Hoefler Text', 'Didot', Georgia, serif", fontWeight: "bold", fontSize: "1.6em", lineHeight: 1, marginRight: "2px", verticalAlign: "baseline" }}>{first}</span>
      {rest}
    </span>
  );
}
